/*
 *	Copyright (c) 2018 SGS Europe. All rights reserved.
 *
 *	This program is the CONFIDENTIAL and PROPRIETARY property of 2018 SGS Europe.
 *	Any unauthorised use, reproduction, or transfer of this program is strictly
 *	prohibited.
 *
 *	File Name:	MarqueeService.js
 *
 *	Description:	A helper class to process coordinates with respect to centre shift, drag offset and rotation.
 *					Also to render a marquee image on the artwork canvas. Will render rectangle, ellipse & free draw shapes.
 *
 *	History:		16-FEB-18	N Settle	Initial implementation.
 *					22-FEB-18	P Raper		Greenlight UAT enhancements including double click issue.
 *					28-FEB-18	N Settle	Some code-tidy and some fixes raised by JSLint (DR208).
 *					16-MAR-18	N Settle	Fixed issue where user is unable to find an annotation on a click if the
 *											marqueeService is not supposed to process clicks (DR208).
 *					26-OCT-18   R Pavolas   Global refactoring Bind/Unbind to On/Off
 *					21-NOV-18   R Pavolas   Made the marquee zoom rebind the mouseHover when marqueeZoom is selected.
 *					26-NOV-18   R Pavolas   Added bypass for transformMarqueeCoordinatesForRotation.
 */

var g_oMarquee = {};

(function () {
	"use strict";

	function MarqueeService() {
		this.g_iOrigin_x = 0;
		this.g_iOrigin_y = 0;
		this.g_iX = 0;
		this.g_iY = 0;
	}

    /**
     * Initiates the marquee drawing.
     * @param {event} event - The event.
     * @param {int} iAnnotationType - The type of annotation.
     * @param {object} oAnnotationViewer - THe annotation viewer.
     * @param {object} oArtworkViewer - The artwork viewer.
     * @param {object} oSyncCanvas - The synced canvases if any.
     * @param {float} fMarqueeDrawFunction - Function to call when drawing.
     * @param {gloat} fMarqueePointerUpFunction - Function to call when finished drawing.
     * @param {bool} bDrawMarqueeCondition - Condition for the marquee if any.
     * @param {bool} bOverridePointSelectionCheck - Whether to override the selection check.
     * @param {object} oDrawRectangleRenderDetails - Rectangle render details.
     * @param {bool} bInitiate - Whether to initiate.
     */
	MarqueeService.prototype.initiateMarqueeDraw = function (event, iAnnotationType, oAnnotationViewer, oArtworkViewer, oSyncCanvas, fMarqueeDrawFunction, fMarqueePointerUpFunction, bDrawMarqueeCondition, bOverridePointSelectionCheck, oDrawRectangleRenderDetails, bInitiate) {

		// Get the anchor point.
		var coords = getClickCoords(event, oAnnotationViewer);
		this.g_iOrigin_x = coords[0];
		this.g_iOrigin_y = coords[1];
		this.g_iX = coords[0];
		this.g_iY = coords[1];
		var that = this;

		oAnnotationViewer.getRatio();
		oArtworkViewer.synchroniseZoomPanProperties(oAnnotationViewer);

		// As they move, draw the marquee from anchor to current position...unless it is a Point or Global marquee type. These are done off the 'pointerup' event.
		// All marquee work is done on the Annotation Viewer.
		if (!(iAnnotationType === eAnnotationTypes.Postit || iAnnotationType === eAnnotationTypes.Global)) {
			changeCanvasFunction(".annotationViewer", "pointermove", function (event) {
				g_oMarquee.marqueeDraw(event, iAnnotationType, oAnnotationViewer, oArtworkViewer, fMarqueeDrawFunction, oDrawRectangleRenderDetails);
			});
		}

		// Once the mouse is released, measure the drawn area.
		changeCanvasFunction(".annotationViewer", "pointerup", function (event) {
			$(".annotationViewer").off("pointermove");

			if (bInitiate || (Math.abs(that.g_iOrigin_x - that.g_iX) !== 0 || Math.abs(that.g_iOrigin_y - that.g_iY) !== 0)) {
				$(".annotationViewer").off("click");		// As the mouse has moved, we no longer need to check 'findAnnotations'.

				// The Point (i.e. Post-it Annotation) or Global marquees are drawn from a click so there should be no 'pointermove' event.
				// So we fire the marqueeDraw function for these two types as it won't have been fired previously.
				if (iAnnotationType === eAnnotationTypes.Postit || iAnnotationType === eAnnotationTypes.Global) {
					g_oMarquee.marqueeDraw(event, iAnnotationType, oAnnotationViewer, oArtworkViewer, fMarqueeDrawFunction, oDrawRectangleRenderDetails);
				}

                g_oMarquee.marqueePointerUp(event, oArtworkViewer, oSyncCanvas, bDrawMarqueeCondition, bOverridePointSelectionCheck, fMarqueePointerUpFunction);

                // If it's the marquee zoom, rebind mouseHover
                if ($("#btnZoomMarquee").parent().hasClass("selectedButton")) {
                    changeCanvasFunction("#AnnotationViewer", "pointermove.annotationUI", function (event) {
                        AnnotationAction.prototype.mouseMove(event, oArtworkViewer, oAnnotationViewer);
                    });
                }                
			}
			else {
                oAnnotationViewer.redraw(false);
                g_oAnnotations.findAnnotation(event, oArtworkViewer, oAnnotationViewer);
			}
		});
	};

	/**
	 * Initiates a marquee wheel zoom.
	 * @param {object} event - The event.
	 * @param {object} oAnnotationViewer - The annotation viewer.
	 * @param {object} oArtworkViewer - The artwork viewer.
	 * @param {object} oSyncCanvas - The list of sync cavnas.
	 * @param {function} fMarqueePointerUpFunction - The marque pointer up function to be used.
	 * @param {int} iScrollPreference - The scroll preference to be used.
	 */
	MarqueeService.prototype.initiateMarqueeWheelZoom = function (event, oAnnotationViewer, oArtworkViewer, oSyncCanvas, fMarqueePointerUpFunction, iScrollPreference) {

		// Get the anchor point of the mouse.
		var coords = getClickCoords(event, oAnnotationViewer);
		this.g_iOrigin_x = coords[0];
		this.g_iOrigin_y = coords[1];
		this.g_iX = coords[0];
		this.g_iY = coords[1];

		// If this is a smart zoom.
		if (iScrollPreference == eWheelZoomType.Smart) {

			// Calculate the marquee offset required to keep the mouse on its current location in the document.
			this.g_iOrigin_x += ((oArtworkViewer.g_oCanvas.width / 2) - coords[0]) / oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;
			this.g_iOrigin_y += ((oArtworkViewer.g_oCanvas.height / 2) - coords[1]) / oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;
			this.g_iX += ((oArtworkViewer.g_oCanvas.width / 2) - coords[0]) / oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;
			this.g_iY += ((oArtworkViewer.g_oCanvas.height / 2) - coords[1]) / oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;
		}

		// Get the compare ratio of the canvas.
		oAnnotationViewer.getRatio();

		// Call the marquee pointer up immediately.
		g_oMarquee.marqueePointerUp(event, oArtworkViewer, oSyncCanvas, false, true, fMarqueePointerUpFunction);
	};

	/**
	 * Initiates a marquee wheel zoom out.
	 * @param {object} event - The event.
	 * @param {object} oAnnotationViewer - The annotation viewer.
	 * @param {object} oArtworkViewer - The artwork viewer.
	 * @param {object} oSyncCanvas - The list of sync cavnas.
	 * @param {function} fMarqueePointerUpFunction - The marque pointer up function to be used.
	 * @param {int} iScrollPreference - The scroll preference to be used.
	 */
	MarqueeService.prototype.initiateMarqueeWheelZoomOut = function (event, oAnnotationViewer, oArtworkViewer, oSyncCanvas, fMarqueePointerUpFunction, iScrollPreference) {

		// Get the anchor point of the mouse.
		var coords = getClickCoords(event, oAnnotationViewer);
		this.g_iOrigin_x = coords[0];
		this.g_iOrigin_y = coords[1];
		this.g_iX = coords[0];
		this.g_iY = coords[1];

		// If this is a smart zoom.
		if (iScrollPreference == eWheelZoomType.Smart) {
			
			// Calculate the marquee offset required to keep the mouse on its current location in the document.
			this.g_iOrigin_x += ((oArtworkViewer.g_oCanvas.width / 2) - coords[0]) * oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;
			this.g_iOrigin_y += ((oArtworkViewer.g_oCanvas.height / 2) - coords[1]) * oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;
			this.g_iX += ((oArtworkViewer.g_oCanvas.width / 2) - coords[0]) * oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;
			this.g_iY += ((oArtworkViewer.g_oCanvas.height / 2) - coords[1]) * oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;
		}

		// Get the compare ratio of the canvas.
		oAnnotationViewer.getRatio();

		// Call the marquee pointer up immediately.
		g_oMarquee.marqueePointerUp(event, oArtworkViewer, oSyncCanvas, false, true, fMarqueePointerUpFunction);
	};

    /**
     * The marquee draw pointermove event
     * @param {event} event - the event.
     * @param {enum} iAnnotationType - The annotation type.
     * @param {object} oAnnotationViewer - The annotation viewer.
     * @param {object} oArtworkViewer - The artwork viewer.
     * @param {function} fImplementationSpecificMarqueeDrawFunction - What function to invoke for specific implementation if any.
     * @param {object} oDrawRectangleRenderDetails - Rectangle rendering details object.
     */
	MarqueeService.prototype.marqueeDraw = function (event, iAnnotationType, oAnnotationViewer, oArtworkViewer, fImplementationSpecificMarqueeDrawFunction, oDrawRectangleRenderDetails) {
        var coords = getClickCoords(event, oAnnotationViewer);
		this.g_iX = coords[0];
		this.g_iY = coords[1];

		var iCanvasWidth = oAnnotationViewer.g_oCanvas.width;
		var iCanvasHeight = oAnnotationViewer.g_oCanvas.height;
		var dCanvasHalfWidth = iCanvasWidth / 2;
		var dCanvasHalfHeight = iCanvasHeight / 2;

		var margin = oAnnotationViewer.c_iMargin;

		$(".speechbubble").hide();

		// Have we stayed within the bounds of the canvas, using a 10px margin?
		if ((this.g_iX > margin && this.g_iX < iCanvasWidth - margin) && (this.g_iY > margin && this.g_iY < iCanvasHeight - margin)) {
			var oRenderValues;

			switch (iAnnotationType) {
				case eAnnotationTypes.Postit:
					oRenderValues = g_oMarquee.renderPointMarquee(oAnnotationViewer, oArtworkViewer, iCanvasWidth, iCanvasHeight, oDrawRectangleRenderDetails);
					break;
				case eAnnotationTypes.Freedraw:
					oRenderValues = g_oMarquee.renderFreedrawMarquee(oAnnotationViewer, oArtworkViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight, oDrawRectangleRenderDetails);
					break;
				case eAnnotationTypes.TextMeasurement:
                case eAnnotationTypes.PointToPointMeasurement:
				case eAnnotationTypes.HorizontalCalliperMeasurement:
				case eAnnotationTypes.VerticalCalliperMeasurement:
				case eAnnotationTypes.AreaMeasurement:
				case eAnnotationTypes.ColourDensityMeasurement:
				case eAnnotationTypes.BarcodeMeasurement:
                case eAnnotationTypes.Rectangle:
					oRenderValues = g_oMarquee.renderRectangleMarquee(oAnnotationViewer, oArtworkViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight, oDrawRectangleRenderDetails);
					break;
				case eAnnotationTypes.Ellipse:
					oRenderValues = g_oMarquee.renderEllipseMarquee(oAnnotationViewer, oArtworkViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight, oDrawRectangleRenderDetails);
					break;
				case eAnnotationTypes.Line:
					oRenderValues = g_oMarquee.renderLineMarquee(oAnnotationViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight, oDrawRectangleRenderDetails);
					break;
				case eAnnotationTypes.Global:
					oRenderValues = g_oMarquee.renderGlobalMarquee(oAnnotationViewer, oArtworkViewer, oDrawRectangleRenderDetails);
					break;
				default:
					console.log("g_oMarquee.marqueeDraw: Unknown annotation type! (value = " + iAnnotationType + ")");
			}

			if (oRenderValues !== undefined && oRenderValues !== null) {
				/*
				DO THE IMPLEMENTATION SPECIFIC STUFF HERE!!!!
				*/
				if (fImplementationSpecificMarqueeDrawFunction !== undefined && fImplementationSpecificMarqueeDrawFunction !== null) {
					fImplementationSpecificMarqueeDrawFunction(oArtworkViewer,
																oAnnotationViewer,
																oRenderValues.oCanvasContext,
																oRenderValues.iCoordinateDistance_X,
																oRenderValues.iCoordinateDistance_Y,
																oRenderValues.iBeginPoint_X,
																oRenderValues.iBeginPoint_Y,
																iCanvasWidth,
																iCanvasHeight,
																dCanvasHalfWidth,
																dCanvasHalfHeight);
				}

                oRenderValues.oCanvasContext.restore();               
			}
		}
		else {		// if not within bounds of canvas, we want to cancel the marquee draw / zoom.
			$(".annotationViewer").off("pointermove");
			$(".annotationViewer").off("pointerup");
			oAnnotationViewer.dragDiscontinue(false, []);
			g_oAnnotations.redrawAnnotations(oArtworkViewer, oAnnotationViewer);
			oArtworkViewer.redraw(false);
		}
	};

    /**
     * Marquee measurement pointerup event.
     * @param {event} event - The event.
     * @param {object} oArtworkViewer - The artwork viewer.
     * @param {object} oSyncCanvas - The synced comparison canvases if any.
     * @param {bool} bDrawMarqueeCondition - Whether to draw the marquee condition.
     * @param {bool} bOverridePointSelectionCheck - Whether to override the point selection check.
     * @param {function} fImplementationSpecificMarqueePointerUpFunction - What implementation specific function to invoke if any.
     */
	MarqueeService.prototype.marqueePointerUp = function (event, oArtworkViewer, oSyncCanvas, bDrawMarqueeCondition, bOverridePointSelectionCheck, fImplementationSpecificMarqueePointerUpFunction) {
		
		// Only execute the marquee measure if the difference between dragged coordinates is not 0 (e.g. mouse click on point)
		// But for densitometer and text search we do allow a click measurement
		if ((((g_oMarquee.g_iOrigin_x !== g_oMarquee.g_iX) || (g_oMarquee.g_iOrigin_y !== g_oMarquee.g_iY)) && bDrawMarqueeCondition) || bOverridePointSelectionCheck) {
			
			// Transform the marquee co-ordinates for rotation.
			var oTransformedCoordinateValues = g_oMarquee.transformMarqueeCoordinatesForRotation(oArtworkViewer, true);

			/*
			DO THE IMPLEMENTATION SPECIFIC STUFF HERE!!!!
			*/
			if (fImplementationSpecificMarqueePointerUpFunction !== undefined && fImplementationSpecificMarqueePointerUpFunction !== null) {
				fImplementationSpecificMarqueePointerUpFunction(event,
																oArtworkViewer,
																oSyncCanvas,
																oTransformedCoordinateValues.iBeginPoint_X,
																oTransformedCoordinateValues.iBeginPoint_Y,
																oTransformedCoordinateValues.iEndPoint_X,
																oTransformedCoordinateValues.iEndPoint_Y,
																oTransformedCoordinateValues.iDragOffset_X,
																oTransformedCoordinateValues.iDragOffset_Y,
																oTransformedCoordinateValues.iCenterShift_X,
																oTransformedCoordinateValues.iCenterShift_Y,
																oTransformedCoordinateValues.iImageWidth,
																oTransformedCoordinateValues.iImageHeight);
			}

			// Unbind the pointer up event.
			$(".annotationViewer").off("pointerup");
		}
		else {
			// the user has clicked rather than drawn a marquee.
			// Revert to defaults, i.e. find an annotation.
			//oArtworkViewer.clearButtonHighlights();
			changeCanvasFunction(".annotationViewer");
		}
	};

	MarqueeService.prototype.renderRectangleMarquee = function (oAnnotationViewer, oArtworkViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight, oDrawRectangleRenderDetails) {
		var canvas = oAnnotationViewer.g_oCanvas;
		var canvasContext = oAnnotationViewer.g_oContext;

		var beginPoint_x = Math.min(this.g_iOrigin_x, this.g_iX);
		var beginPoint_y = Math.min(this.g_iOrigin_y, this.g_iY);
		var iCoordinateDistance_x = Math.abs(this.g_iOrigin_x - this.g_iX);
		var iCoordinateDistance_y = Math.abs(this.g_iOrigin_y - this.g_iY);

		this.derotateCanvasContext(canvasContext, oAnnotationViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight);
		canvasContext.clearRect(0, 0, iCanvasWidth, iCanvasHeight);
		canvasContext.beginPath();

		if (oDrawRectangleRenderDetails !== undefined && oDrawRectangleRenderDetails !== null) {
			canvasContext.rect(beginPoint_x, beginPoint_y, iCoordinateDistance_x, iCoordinateDistance_y);
			canvasContext.lineWidth = oDrawRectangleRenderDetails.lineWidth;
			canvasContext.strokeStyle = oDrawRectangleRenderDetails.lineColour;
			canvasContext.stroke();
		}
		else {
			canvasContext.rect(0, 0, iCanvasWidth, iCanvasHeight);
			canvasContext.fillStyle = "rgba(0,0,0,0.75)";
			canvasContext.fill();
			canvasContext.clearRect(beginPoint_x, beginPoint_y, iCoordinateDistance_x, iCoordinateDistance_y);
		}

		var returnValues = {
			oCanvasContext: canvasContext,
			iCoordinateDistance_X: iCoordinateDistance_x,
			iCoordinateDistance_Y: iCoordinateDistance_y,
			iBeginPoint_X: beginPoint_x,
			iBeginPoint_Y: beginPoint_y
		};

		return returnValues;
	};

	MarqueeService.prototype.renderLineMarquee = function (oAnnotationViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight, oDrawRectangleRenderDetails) {
		// Setup some variables to hold the Annotation/Artwork Canvas
		var canvasContext = oAnnotationViewer.g_oCanvas;

		var beginPoint_x = g_oMarquee.g_iOrigin_x;
		var beginPoint_y = g_oMarquee.g_iOrigin_y;
		var iCoordinateDistance_x = g_oMarquee.g_iX - g_oMarquee.g_iOrigin_x;
		var iCoordinateDistance_y = g_oMarquee.g_iY - g_oMarquee.g_iOrigin_y;

		this.derotateCanvasContext(canvasContext, oAnnotationViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight);
		canvasContext.clearRect(0, 0, iCanvasWidth, iCanvasHeight);
		canvasContext.beginPath();
		canvasContext.moveTo(beginPoint_x, beginPoint_y);
		canvasContext.lineTo(beginPoint_x + iCoordinateDistance_x, beginPoint_y + iCoordinateDistance_y);
		canvasContext.lineWidth = oDrawRectangleRenderDetails.lineWidth;
		canvasContext.strokeStyle = oDrawRectangleRenderDetails.lineColour;
		canvasContext.stroke();

		var returnValues = {
			oCanvasContext: canvasContext,
			iCoordinateDistance_X: iCoordinateDistance_x,
			iCoordinateDistance_Y: iCoordinateDistance_y,
			iBeginPoint_X: beginPoint_x,
			iBeginPoint_Y: beginPoint_y
		};
		return returnValues;
	};

	MarqueeService.prototype.renderPointMarquee = function (oAnnotationViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight, oDrawRectangleRenderDetails) {
		// Link to the Annotation Canvas
		var canvasContext = oAnnotationViewer.g_oContext;

		// These offsets are set to correctly position the Post it note on the page.
		var g_oPostit = {};
		g_oPostit.heightOffset = 13;
		g_oPostit.widthOffset = 3;

		this.derotateCanvasContext(canvasContext, oAnnotationViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight);
		canvasContext.clearRect(0, 0, iCanvasWidth, iCanvasHeight);

		// Draw Image is part of the HTML5 Canvas Spec.
		canvasContext.drawImage(g_oAnnotations.g_oPostitImg,
								g_oMarquee.g_iX - g_oPostit.widthOffset,
								g_oMarquee.g_iY - g_oPostit.heightOffset);

		// Push each point in the coordinates to the array
		var beginPoint_x = g_oMarquee.g_iOrigin_x;
		var beginPoint_y = g_oMarquee.g_iOrigin_y;
		var iCoordinateDistance_x = g_oMarquee.g_iX - g_oMarquee.g_iOrigin_x;
		var iCoordinateDistance_y = g_oMarquee.g_iY - g_oMarquee.g_iOrigin_y;

		var returnValues = {
			oCanvasContext: canvasContext,
			iCoordinateDistance_X: iCoordinateDistance_x,
			iCoordinateDistance_Y: iCoordinateDistance_y,
			iBeginPoint_X: beginPoint_x,
			iBeginPoint_Y: beginPoint_y
		};
		return returnValues;
	};

	MarqueeService.prototype.renderFreedrawMarquee = function (oAnnotationViewer, oArtworkViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight, oDrawRectangleRenderDetails) {
		var canvasContext = oAnnotationViewer.g_oContext;

		this.derotateCanvasContext(canvasContext, oAnnotationViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight);
		canvasContext.beginPath();
		canvasContext.moveTo(g_oMarquee.g_iOrigin_x, g_oMarquee.g_iOrigin_y);
		canvasContext.lineTo(g_oMarquee.g_iX, g_oMarquee.g_iY);
		canvasContext.strokeStyle = oDrawRectangleRenderDetails.lineColour;
		canvasContext.lineWidth = oDrawRectangleRenderDetails.lineWidth;
		canvasContext.stroke();
		canvasContext.closePath();


		var beginPoint_x = g_oMarquee.g_iX;
		var beginPoint_y = g_oMarquee.g_iY;
		var iCoordinateDistance_x = g_oMarquee.g_iX - g_oMarquee.g_iOrigin_x;
		var iCoordinateDistance_y = g_oMarquee.g_iY - g_oMarquee.g_iOrigin_y;

		var returnValues = {
			oCanvasContext: canvasContext,
			iCoordinateDistance_X: iCoordinateDistance_x,
			iCoordinateDistance_Y: iCoordinateDistance_y,
			iBeginPoint_X: beginPoint_x,
			iBeginPoint_Y: beginPoint_y
		};

		return returnValues;
	};

	MarqueeService.prototype.renderEllipseMarquee = function (oAnnotationViewer, oArtworkViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight, oDrawRectangleRenderDetails) {
		var canvasContext = oAnnotationViewer.g_oContext;

		var beginPoint_x = Math.min(g_oMarquee.g_iOrigin_x, g_oMarquee.g_iX);
		var beginPoint_y = Math.min(g_oMarquee.g_iOrigin_y, g_oMarquee.g_iY);
		var iCoordinateDistance_x = Math.abs(g_oMarquee.g_iX - g_oMarquee.g_iOrigin_x);
		var iCoordinateDistance_y = Math.abs(g_oMarquee.g_iY - g_oMarquee.g_iOrigin_y);
		var halfPoint_x = beginPoint_x + (iCoordinateDistance_x / 2);
		var halfPoint_y = beginPoint_y + (iCoordinateDistance_y / 2);

		var width_over_2 = iCoordinateDistance_x / 2;
		var width_two_thirds = iCoordinateDistance_x * 2 / 3;
		var height_over_2 = iCoordinateDistance_y / 2;

		this.derotateCanvasContext(canvasContext, oAnnotationViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight);
		canvasContext.clearRect(0, 0, iCanvasWidth, iCanvasHeight);

		canvasContext.beginPath();
		canvasContext.moveTo(halfPoint_x, halfPoint_y - height_over_2);
		canvasContext.bezierCurveTo(halfPoint_x + width_two_thirds, halfPoint_y - height_over_2, halfPoint_x + width_two_thirds, halfPoint_y + height_over_2, halfPoint_x, halfPoint_y + height_over_2);
		canvasContext.bezierCurveTo(halfPoint_x - width_two_thirds, halfPoint_y + height_over_2, halfPoint_x - width_two_thirds, halfPoint_y - height_over_2, halfPoint_x, halfPoint_y - height_over_2);
		canvasContext.closePath();
		canvasContext.stroke();

		canvasContext.strokeStyle = oDrawRectangleRenderDetails.lineColour;
		canvasContext.lineWidth = oDrawRectangleRenderDetails.lineWidth;
		canvasContext.stroke();

		var returnValues = {
			oCanvasContext: canvasContext,
			iCoordinateDistance_X: iCoordinateDistance_x,
			iCoordinateDistance_Y: iCoordinateDistance_y,
			iBeginPoint_X: beginPoint_x,
			iBeginPoint_Y: beginPoint_y
		};
		return returnValues;
	};

	MarqueeService.prototype.renderGlobalMarquee = function (oAnnotationViewer, oArtworkViewer, oDrawRectangleRenderDetails) {
		// Get the annotation context
		var canvasContext = oAnnotationViewer.g_oContext;

		// Draw the rectangle.
		var beginPoint_x = round(0 + oArtworkViewer.g_iCenterShift_x);
		var beginPoint_y = round(1 + oArtworkViewer.g_iCenterShift_y);
		var iCoordinateDistance_x = oArtworkViewer.g_oImage.width;
		var iCoordinateDistance_y = oArtworkViewer.g_oImage.height;

		canvasContext.beginPath();
		canvasContext.rect(beginPoint_x, beginPoint_y, iCoordinateDistance_x, iCoordinateDistance_y);
		canvasContext.lineWidth = oDrawRectangleRenderDetails.lineWidth;
		canvasContext.strokeStyle = oDrawRectangleRenderDetails.lineColour;
		canvasContext.stroke();

		g_oMarquee.g_iOrigin_x = beginPoint_x;
		g_oMarquee.g_iOrigin_y = beginPoint_y;
		g_oMarquee.g_iX = beginPoint_x + iCoordinateDistance_x;
		g_oMarquee.g_iY = beginPoint_y + iCoordinateDistance_y;

		var returnValues = {
			oCanvasContext: canvasContext,
			iCoordinateDistance_X: iCoordinateDistance_x,
			iCoordinateDistance_Y: iCoordinateDistance_y,
			iBeginPoint_X: beginPoint_x,
			iBeginPoint_Y: beginPoint_y
		};

		return returnValues;
	};

    MarqueeService.prototype.transformMarqueeCoordinatesForRotation = function (oArtworkViewer, bAllowReverseCoordinates, bRespectRealtimeDensity) {
		var beginPoint_x;
		var beginPoint_y;
		var endPoint_x;
		var endPoint_y;
		var iImageWidth;
		var iImageHeight;
		var iCenterShift_x;
		var iCenterShift_y;
		var iDragOffset_x;
        var iDragOffset_y;
        bRespectRealtimeDensity = typeof bRespectRealtimeDensity === "undefined" ? true : bRespectRealtimeDensity;

		// Get the compare rotation of the canvas.
		var iCompareRotation = g_oCompare.getCompareRotation(oArtworkViewer);

		if (bRespectRealtimeDensity && (oArtworkViewer.isViewerUsingRealtimeRender() && g_oTechTools.g_iSelectedMeasurementType == g_oTechTools.g_eMeasurementTypes.COLOURDENSITY)) {

			// Get the image width and height.
			iImageWidth = oArtworkViewer.g_oCanvas.width;
			iImageHeight = oArtworkViewer.g_oCanvas.height;

			// If we have a compare rotation of 90 or 270, swap the image width and height.
			if (iCompareRotation == 90 || iCompareRotation == 270) {
				iImageWidth = oArtworkViewer.g_oCanvas.height;
				iImageHeight = oArtworkViewer.g_oCanvas.width;
			}

			// Get the center shift.
			iCenterShift_x = 0;
			iCenterShift_y = 0;

			// Get the drag offset.
			iDragOffset_x = 0;
			iDragOffset_y = 0;
		}
		else {

			// Get the image width and height.
			iImageWidth = oArtworkViewer.g_iScaledImageWidth;
			iImageHeight = oArtworkViewer.g_iScaledImageHeight;

			// If we have a compare rotation of 90 or 270, swap the image width and height.
			if (iCompareRotation == 90 || iCompareRotation == 270) {
				iImageWidth = oArtworkViewer.g_iScaledImageHeight;
				iImageHeight = oArtworkViewer.g_iScaledImageWidth;
			}

			// Get the center shift.
			iCenterShift_x = oArtworkViewer.g_iCenterShift_x;
			iCenterShift_y = oArtworkViewer.g_iCenterShift_y;

			// Get the drag offset.
			iDragOffset_x = oArtworkViewer.g_iDragOffset_x;
			iDragOffset_y = oArtworkViewer.g_iDragOffset_y;
		}

		// Calculate the image width and height.
		var iImageObjW = Math.max(iImageWidth, oArtworkViewer.g_iCompareImage_x);
		var iImageObjH = Math.max(iImageHeight, oArtworkViewer.g_iCompareImage_y);

		// Find the bounds of the dragged marquee.
		if (bAllowReverseCoordinates) {
			beginPoint_x = Math.min(g_oMarquee.g_iOrigin_x, g_oMarquee.g_iX);
			beginPoint_y = Math.min(g_oMarquee.g_iOrigin_y, g_oMarquee.g_iY);

			endPoint_x = Math.max(g_oMarquee.g_iOrigin_x, g_oMarquee.g_iX);
			endPoint_y = Math.max(g_oMarquee.g_iOrigin_y, g_oMarquee.g_iY);
		}
		else {
			beginPoint_x = g_oMarquee.g_iOrigin_x;
			beginPoint_y = g_oMarquee.g_iOrigin_y;

			endPoint_x = g_oMarquee.g_iX;
			endPoint_y = g_oMarquee.g_iY;
		}

		var diffX = endPoint_x - beginPoint_x;
		var diffY = endPoint_y - beginPoint_y;

		oArtworkViewer.g_iTempDragOffset_x = 0;
		oArtworkViewer.g_iTempDragOffset_y = 0;

		if (!bRespectRealtimeDensity || !oArtworkViewer.isViewerUsingRealtimeRender() || !(g_oTechTools.g_iSelectedMeasurementType == g_oTechTools.g_eMeasurementTypes.COLOURDENSITY)) {
			// Used to flip the drag offset when rotated
			var tempx;
			var tempy;

			switch (oArtworkViewer.g_iRotDegrees) {
				case 90:
					// Remove the CenterShift compensation
					tempx = beginPoint_x - iCenterShift_x - ((iImageObjW - iImageObjH) / 2);
					// Work the coordinates back from the opposite image edge
					tempx = iImageObjH - tempx;
					// Then reapply the opposite CenterShift
					tempx += iCenterShift_y;
					// Reverse the CenterShift compensation.
					tempy = beginPoint_y - iCenterShift_y + iCenterShift_x + ((iImageObjW - iImageObjH) / 2);
					// x becomes y and vice versa
					beginPoint_x = tempy;
					beginPoint_y = tempx;
					// We also need to subtract the difference from y ONLY since the begin point is now greater than the endpoint!
					endPoint_x = beginPoint_x + diffY;
					endPoint_y = beginPoint_y - diffX;

					oArtworkViewer.g_iTempDragOffset_x -= iDragOffset_y;
					oArtworkViewer.g_iTempDragOffset_x += iDragOffset_x;
					oArtworkViewer.g_iTempDragOffset_y += (iDragOffset_y);
					oArtworkViewer.g_iTempDragOffset_y += (iDragOffset_x);
					break;

				case 180:
					// We need to work the calculation backwards for both x and y (from the end of the image rather than the start)
					tempx = (iImageObjW - (beginPoint_x - iCenterShift_x)) + iCenterShift_x;
					tempy = (iImageObjH - (beginPoint_y - iCenterShift_y)) + iCenterShift_y;
					beginPoint_x = tempx;
					beginPoint_y = tempy;
					// We also need to subtract the difference since the begin point is now greater than the endpoint!
					endPoint_x = beginPoint_x - diffX;
					endPoint_y = beginPoint_y - diffY;

					oArtworkViewer.g_iTempDragOffset_x += (iDragOffset_x * 2);
					oArtworkViewer.g_iTempDragOffset_y += (iDragOffset_y * 2);
					break;

				case 270:
					// Remove the CenterShift compensation
					tempy = beginPoint_y - iCenterShift_y - ((iImageObjH - iImageObjW) / 2);
					// Work the coordinates back from the opposite image edge
					tempy = iImageObjW - tempy;
					// Then reapply the opposite CenterShift
					tempy += iCenterShift_x;
					// Reverse the CenterShift compensation.
					tempx = beginPoint_x - iCenterShift_x + iCenterShift_y + ((iImageObjH - iImageObjW) / 2);
					// x becomes y and vice versa
					beginPoint_x = tempy;
					beginPoint_y = tempx;
					// We also need to subtract the difference from x ONLY since the begin point is now greater than the endpoint!
					endPoint_x = beginPoint_x - diffY;
					endPoint_y = beginPoint_y + diffX;

					oArtworkViewer.g_iTempDragOffset_x += iDragOffset_y;
					oArtworkViewer.g_iTempDragOffset_x += iDragOffset_x;
					oArtworkViewer.g_iTempDragOffset_y += (iDragOffset_y);
					oArtworkViewer.g_iTempDragOffset_y -= (iDragOffset_x);
					break;
			}
		}

		var returnValues = {
			iBeginPoint_X: beginPoint_x,
			iBeginPoint_Y: beginPoint_y,
			iEndPoint_X: endPoint_x,
			iEndPoint_Y: endPoint_y,
			iCenterShift_X: iCenterShift_x,
			iCenterShift_Y: iCenterShift_y,
			iDragOffset_X: iDragOffset_x,
			iDragOffset_Y: iDragOffset_y,
			iImageWidth: iImageObjW,
			iImageHeight: iImageObjH
		};

		return returnValues;
	};

	MarqueeService.prototype.isMarqueeWithinArtwork = function (oArtworkViewer) {
		var beginPointX = g_oMarquee.g_iOrigin_x;
		var beginPointY = g_oMarquee.g_iOrigin_y;
		var endPointX = g_oMarquee.g_iX;
		var endPointY = g_oMarquee.g_iY;
		var scaledImageWidth = oArtworkViewer.g_iScaledImageWidth;
		var scaledImageHeight = oArtworkViewer.g_iScaledImageHeight;

		// Calculate the compare offset of the canvas.
		var compareOffset_x, compareOffset_y;
		var co = g_oCompare.calculateCompareOffsetInPx(oArtworkViewer);
		compareOffset_x = co.x;
		compareOffset_y = co.y;

		// Get the compare rotation of the canvas.
		var iCompareRotation = g_oCompare.getCompareRotation(this);

		// If we have a compare rotation of 90 or 270, swap the image width and height.
		if (iCompareRotation == 90 || iCompareRotation == 270) {
			scaledImageWidth = oArtworkViewer.g_iScaledImageHeight;
			scaledImageHeight = oArtworkViewer.g_iScaledImageWidth;
		}

		// Create a rectangle around the artwork.
		oArtworkViewer.g_oContext.beginPath();
		oArtworkViewer.g_oContext.rect(oArtworkViewer.g_iCenterShift_x + compareOffset_x, oArtworkViewer.g_iCenterShift_y + compareOffset_y, scaledImageWidth, scaledImageHeight);
		oArtworkViewer.g_oContext.fillStyle = "rgba(0,0,0,0)";
		oArtworkViewer.g_oContext.fill();
		oArtworkViewer.g_oContext.closePath();

		// Determine if the marquee is within the artwork.
		var bMarqueeWithinArtwork = false;

		// Calculate the mid point of the marquee.
		var iCentrePointX = beginPointX + ((endPointX - beginPointX) / 2);
		var iCentrePointY = beginPointY + ((endPointY - beginPointY) / 2);

		// Check each of the major points of the marquee.
		if (oArtworkViewer.g_oContext.isPointInPath(beginPointX, beginPointY)) {
			// North West Point.
			bMarqueeWithinArtwork = true;
		}
		else if (oArtworkViewer.g_oContext.isPointInPath(iCentrePointX, beginPointY)) {
			// North Point.
			bMarqueeWithinArtwork = true;
		}
		else if (oArtworkViewer.g_oContext.isPointInPath(endPointX, beginPointY)) {
			// North East Point.
			bMarqueeWithinArtwork = true;
		}
		else if (oArtworkViewer.g_oContext.isPointInPath(endPointX, iCentrePointY)) {
			// East Point.
			bMarqueeWithinArtwork = true;
		}
		else if (oArtworkViewer.g_oContext.isPointInPath(endPointX, endPointY)) {
			// South East Point.
			bMarqueeWithinArtwork = true;
		}
		else if (oArtworkViewer.g_oContext.isPointInPath(iCentrePointX, endPointY)) {
			// South Point.
			bMarqueeWithinArtwork = true;
		}
		else if (oArtworkViewer.g_oContext.isPointInPath(beginPointX, endPointY)) {
			// South West Point.
			bMarqueeWithinArtwork = true;
		}
		else if (oArtworkViewer.g_oContext.isPointInPath(beginPointX, iCentrePointY)) {
			// West Point.
			bMarqueeWithinArtwork = true;
		}
		else if (oArtworkViewer.g_oContext.isPointInPath(iCentrePointX, iCentrePointY)) {
			// Centre Point.
			bMarqueeWithinArtwork = true;
		}

		// Return the result.
		return bMarqueeWithinArtwork;
	}

	MarqueeService.prototype.derotateCanvasContext = function (canvasContext, oAnnotationViewer, iCanvasWidth, iCanvasHeight, dCanvasHalfWidth, dCanvasHalfHeight) {
		canvasContext.save();
		canvasContext.translate(dCanvasHalfWidth, dCanvasHalfHeight);
		canvasContext.rotate(-oAnnotationViewer.g_iRotDegrees * Math.PI / 180);
		canvasContext.translate(-dCanvasHalfWidth, -dCanvasHalfHeight);
	};

	// Add the Marquee Service object to the window.
	window.MarqueeService = MarqueeService;
}());